import {Injectable} from '@angular/core';
import {Event} from '@api/models/event/event';
import {LivestreamStatus} from '@api/models/livestream/streaming-info';
import {StreamingHttpService} from '@api/http/streaming/streaming-http.service';
import {PickerItem} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {SportTypeService} from '@api/services/sport-type/sport-type.service';
import {SportType} from '@lib/models/sport-type/sport-type';
import {VideosHttpService} from '@api/http/videos/videos-http.service';
import {BaseCardService} from '@lib-modules/cards/services/base-card/base-card.service';

export enum VideoContentType {
  ActiveStreams = 'active_streams', // Live + Upcoming
  LiveStreams = 'live_streams',
  Replay = 'replay',
  Highlights = 'highlights',
  UpcomingStreams = 'upcoming_streams',
}

@Injectable({
  providedIn: 'root'
})

export class VideoCardService extends BaseCardService {

  private forcedSportsType: SportType;

  constructor(
    private streamingHttp: StreamingHttpService,
  ) {
    super();
  }

  // Matches
  public async fetchData(
    contentType: VideoContentType,
    item?: PickerItem,
    forcedSportsType?: SportType,
    skip = 0,
    limit = 12,
    queryParams: any = {},
  ): Promise<Event[]> {
    this.forcedSportsType = forcedSportsType;
    this.init(skip, limit, item, queryParams);

    switch (contentType) {
      case VideoContentType.ActiveStreams:
        // TODO: Multiple callbacks from different cause shared state concurrency, fix shared variables in base class
        const live = await this.getLivestreams(LivestreamStatus.Live, item);
        const upcoming = await this.getLivestreams(LivestreamStatus.Upcoming, item)
        const streams = live.concat(upcoming);
        return streams.slice(0, (this.skip + this.limit));
      case VideoContentType.LiveStreams:
        return this.getLivestreams(LivestreamStatus.Live);
      case VideoContentType.UpcomingStreams:
        return this.getLivestreams(LivestreamStatus.Upcoming);
      case VideoContentType.Replay:
        return this.getLivestreams(LivestreamStatus.Replay);
      case VideoContentType.Highlights:
        return this.getLivestreams(LivestreamStatus.Highlights);
    }
  }

  private async getLivestreams(status: LivestreamStatus, item?: PickerItem): Promise<Event[]> {
    const params = item ? {[`${item.type.toLowerCase()}id`] : item.objectId} : this.queryParams;
    const response = await this.streamingHttp.getStreamingEventsByStatus(
      status,
      this.skip,
      this.limit,
      params,
      this.sportsType)
    return response?.data || [];
  }

  /**
   * Check if picker item has video content, return true if there are at least 4 replays
   * waiting for be
   * @param item
   */
  public async showVideoContent(item: PickerItem): Promise<boolean> {
    const limit = 4;
    const replayResponse = await this.streamingHttp.getStreamingEventsByStatus(
      LivestreamStatus.Replay, this.skip, limit, {[`${item.type}id`]: item.objectId});
    return replayResponse?.data?.length === limit;
  }

  private get sportsType(): SportType {
    return this.forcedSportsType ?? SportTypeService.selectedSportType;
  }
}
